/**
 * ClinicalOversightPage — Chief Medical Director oversight (Dr. Luis, Jun 12 2026).
 *
 * Cross-patient view of all clinical-AI research across every doctor, for the
 * chief_medical_director role. Backed by clinical-research:recent-all (gated on
 * clinical_oversight:read; CMD + sysadmin). The CMD can also open any patient's
 * full chart from the Clinic Tab — this page is the "all research" rollup.
 *
 * Self-gates on clinical_oversight:read.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Eye, Loader2, AlertTriangle, RefreshCcw, Brain, ChevronRight, ChevronDown } from "lucide-react";

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }
function fmtDateTime(d) { if (!d) return "—"; const x = new Date(d); return Number.isNaN(x.getTime()) ? "—" : x.toLocaleString(); }

export default function ClinicalOversightPage({ user, onNavigate }) {
  const canSee = hasPerm(user, "clinical_oversight:read");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [clinicFilter, setClinicFilter] = useState("all");

  const load = useCallback(async () => {
    if (!canSee) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("clinical-research:recent-all", { limit: 100 });
      setRows(Array.isArray(r) ? r : []);
    } catch (e) { setError((e && e.message) || "Failed to load oversight data"); }
    setLoading(false);
  }, [canSee]);

  useEffect(() => { load(); }, [load]);

  if (!canSee) return <div style={{ padding: 24, color: "#5a6478" }}>This view is for the Chief Medical Director.</div>;

  const flagCount = (r) => Array.isArray(r.contraindication_warnings) ? r.contraindication_warnings.length : 0;

  // All three clinics are always offered; plus any others present in the data.
  const clinicOptions = Array.from(new Set([
    "Tijuana", "Cancún", "Puerto Vallarta",
    ...rows.map((r) => r.clinic_name).filter(Boolean),
  ]));
  const visibleRows = clinicFilter === "all" ? rows : rows.filter((r) => r.clinic_name === clinicFilter);

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Eye size={22} color="#2E8B98" />
          <div>
            <h1 style={{ fontSize: 21, margin: 0 }}>Clinical Oversight</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>
              All clinical-AI research across patients and doctors. Open any patient's chart from the Clinic Tab.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)} style={{ ...btnSecondary, cursor: "pointer" }}>
            <option value="all">All clinics</option>
            {clinicOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={load} style={btnSecondary}><RefreshCcw size={13} /> Refresh</button>
        </div>
      </div>

      {error && <div style={errBox}><AlertTriangle size={14} /> {error}</div>}
      {loading ? (
        <div style={{ color: "#9aa4b2", padding: 12 }}><Loader2 size={14} className="spin" /> Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div style={{ fontSize: 13, color: "#9aa4b2", fontStyle: "italic", padding: 12 }}>
          {rows.length === 0
            ? "No clinical-AI research recorded yet. (The engine is enabled by FEATURE_CLINICAL_AI.)"
            : `No research for ${clinicFilter}.`}
        </div>
      ) : (
        <div style={{ border: "1px solid #e1e6ed", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 120px 1fr 120px 60px 28px", gap: 8, padding: "8px 12px", background: "#f7f9fc", borderBottom: "1px solid #e1e6ed", fontSize: 11, textTransform: "uppercase", color: "#6C757D", fontWeight: 700 }}>
            <div>When</div><div>Patient</div><div>Clinic</div><div>Conditions</div><div>Doctor</div><div>Flags</div><div></div>
          </div>
          {visibleRows.map((r) => {
            const open = expanded === r.id;
            const flags = flagCount(r);
            return (
              <div key={r.id} style={{ borderBottom: "1px solid #f0f3f7" }}>
                <div onClick={() => setExpanded(open ? null : r.id)}
                  style={{ display: "grid", gridTemplateColumns: "140px 1fr 120px 1fr 120px 60px 28px", gap: 8, padding: "9px 12px", fontSize: 12.5, cursor: "pointer", alignItems: "center", background: open ? "#f4f7fb" : "#fff" }}>
                  <div style={{ color: "#6C757D" }}>{fmtDateTime(r.created_at)}</div>
                  <div style={{ color: "#1F2937", fontWeight: 600 }}>{r.patient_name || `#${r.contact_id}`}</div>
                  <div style={{ color: "#5a6478" }}>{r.clinic_name || "—"}</div>
                  <div style={{ color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.conditions || "—"}</div>
                  <div style={{ color: "#5a6478" }}>{r.doctor_name || "—"}</div>
                  <div>{flags > 0 ? <span style={{ fontSize: 11, fontWeight: 700, color: "#b00020", background: "#fdecec", border: "1px solid #f0b4b4", padding: "1px 7px", borderRadius: 10 }}>{flags}</span> : <span style={{ color: "#9aa4b2" }}>—</span>}</div>
                  <div style={{ color: "#9aa4b2" }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</div>
                </div>
                {open && (
                  <div style={{ padding: "4px 14px 14px", background: "#fbfcfe" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#2E8B98", fontWeight: 700, margin: "6px 0 4px" }}>
                      <Brain size={12} /> Advisory summary
                    </div>
                    <div style={{ fontSize: 12.5, color: "#1F2937", whiteSpace: "pre-wrap" }}>{r.response_summary || "—"}</div>
                    {flags > 0 && (
                      <div style={{ marginTop: 8, border: "1px solid #f0b4b4", background: "#fdf3f3", borderRadius: 6, padding: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#b00020", marginBottom: 4 }}>⚠ Contraindication warnings</div>
                        {r.contraindication_warnings.map((c, i) => (
                          <div key={i} style={{ fontSize: 12, color: "#7a2222" }}>
                            <b>{c.drug}</b>{c.option ? ` (${c.option})` : ""}: {(c.warnings || []).map((w) => w.message).join("; ")}
                          </div>
                        ))}
                      </div>
                    )}
                    {onNavigate && (
                      <button onClick={() => onNavigate("clinictab")} style={{ ...btnSecondary, marginTop: 10 }}>Open Clinic Tab →</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btnSecondary = { padding: "6px 12px", fontSize: 12.5, fontWeight: 600, background: "#fff", color: "#3a4a5e", border: "1px solid #c9d6e8", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const errBox = { display: "flex", alignItems: "center", gap: 6, background: "#fde8e8", border: "1px solid #f5a5a5", padding: 10, borderRadius: 4, marginBottom: 12, color: "#8b0000", fontSize: 13 };
